import { inject, type InjectionKey } from 'vue';
import type { paths } from './schema';
import type { Session } from '../auth/session.ts';
import { config } from '../config.ts';

/**
 * The dashboard's only way to the API.
 *
 * Typed from `schema.d.ts`, which `scripts/openapi.mjs` generates from the same
 * route table the gateway routes from. So `api.get('/devices/{deviceId}', …)`
 * knows its path parameter, its query parameters and the shape of its answer,
 * and a field removed from a route's response schema is a compile error here
 * rather than `undefined` on a screen.
 */

/**
 * The type plumbing, by inference rather than indexing, because a path without
 * a GET has `get?: never` in the generated types and indexing into that is an
 * error rather than `never`.
 */
type GetOp<P extends keyof paths> = paths[P] extends { get: infer O } ? O : never;
type GetPath = { [P in keyof paths]: GetOp<P> extends never ? never : P }[keyof paths];
type ParamsOf<O> = O extends { parameters: infer X } ? X : never;
export type Ok<P extends GetPath> =
  GetOp<P> extends { responses: { 200: { content: { 'application/json': infer B } } } } ? B : never;

export interface GetOptions<P extends GetPath> {
  path?: ParamsOf<GetOp<P>> extends { path: infer X } ? X : never;
  query?: ParamsOf<GetOp<P>> extends { query?: infer X } ? X : never;
  signal?: AbortSignal;
}

/** The problem kinds from packages/core/problem.ts, plus the one HTTP cannot report. */
export type ProblemKind =
  'unauthenticated' | 'forbidden' | 'not-found' | 'invalid-request' | 'internal' | 'network';

/**
 * A failed request, in RFC 9457 terms.
 *
 * Callers branch on `kind`, never on `detail`: the gateway's wording may change,
 * the problem type URIs may not (chapter 14). `network` is the one kind no
 * server can send, because it means no server answered.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly kind: ProblemKind;
  readonly detail: string;

  constructor(status: number, kind: ProblemKind, detail: string) {
    super(detail);
    this.name = 'ApiError';
    this.status = status;
    this.kind = kind;
    this.detail = detail;
  }
}

function kindFromType(type: unknown, status: number): ProblemKind {
  const known: ProblemKind[] = [
    'unauthenticated',
    'forbidden',
    'not-found',
    'invalid-request',
    'internal',
  ];
  const tail = typeof type === 'string' ? type.split('/').pop() : undefined;
  if (tail && (known as string[]).includes(tail)) return tail as ProblemKind;
  if (status === 401) return 'unauthenticated';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not-found';
  if (status >= 400 && status < 500) return 'invalid-request';
  return 'internal';
}

/** `/devices/{deviceId}` with `{ deviceId: 'x' }` becomes `/devices/x`. */
export function buildUrl(
  path: string,
  params: Record<string, string> | undefined,
  query: Record<string, string | number | undefined> | undefined,
): string {
  const filled = path.replace(/\{(\w+)\}/g, (_, name: string) => {
    const value = params?.[name];
    if (value === undefined) throw new Error(`missing path parameter ${name}`);
    return encodeURIComponent(value);
  });
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query ?? {})) {
    // Absent and empty mean "not set". Sending `search=` would ask the API to
    // filter by the empty string, which is a different question.
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const qs = search.toString();
  return `${config.apiBase}${filled}${qs ? `?${qs}` : ''}`;
}

export interface Api {
  get<P extends GetPath>(path: P, options?: GetOptions<P>): Promise<Ok<P>>;
}

export function createApi(session: Session, fetchImpl: typeof fetch = fetch): Api {
  async function send(url: string, token: string, signal?: AbortSignal): Promise<Response> {
    try {
      return await fetchImpl(url, {
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
        ...(signal ? { signal } : {}),
      });
    } catch (e) {
      // An abort is the caller changing its mind, not a failure. Let it through
      // unchanged so the caller can recognise and ignore it.
      if ((e as Error).name === 'AbortError') throw e;
      throw new ApiError(0, 'network', (e as Error).message);
    }
  }

  return {
    async get(path, options = {}) {
      const url = buildUrl(
        path,
        options.path as Record<string, string> | undefined,
        options.query as Record<string, string | number | undefined> | undefined,
      );

      let res = await send(url, await session.token(), options.signal);
      if (res.status === 401) {
        // One retry with a freshly refreshed token. The common cause is a token
        // that expired between `token()` and the gateway checking it; a second
        // 401 means the session itself is gone, and refresh() has already sent
        // the browser to the login page by then.
        res = await send(url, await session.refresh(), options.signal);
      }

      if (!res.ok) {
        let body: { type?: unknown; detail?: unknown } = {};
        try {
          body = (await res.json()) as typeof body;
        } catch {
          // Not problem+json: a proxy's error page, most likely. The status
          // still says what kind of failure it was.
        }
        throw new ApiError(
          res.status,
          kindFromType(body.type, res.status),
          typeof body.detail === 'string' ? body.detail : res.statusText,
        );
      }
      return (await res.json()) as never;
    },
  };
}

export const ApiKey: InjectionKey<Api> = Symbol('api');
export const SessionKey: InjectionKey<Session> = Symbol('session');

export function useApi(): Api {
  const api = inject(ApiKey);
  if (!api) throw new Error('useApi() outside an app that provided ApiKey');
  return api;
}

export function useSession(): Session {
  const session = inject(SessionKey);
  if (!session) throw new Error('useSession() outside an app that provided SessionKey');
  return session;
}
